import React from "react";
import { Box } from "@/Components/v2/atoms/Box";
import { Typography } from "@/Components/v2/atoms/Typography";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Dialog, DialogContent } from "@/Components/v2/atoms/Dialog";
import { StrategyProfile } from "@mutiny-group/sbo-openapi-client";
import { getStrategyProfile } from "../types";
import { styled } from "@mui/material";
import { useTheme } from "@mui/material";

const BenefitsList = styled(Box)({
  paddingLeft: "21px",
  "& ul": {
    margin: 0,
    paddingLeft: "0.25rem",
  },
  "& li": {
    display: "list-item",
    listStyle: "disc outside",
    marginBottom: "0.25rem",
    fontSize: "14px",
  },
});

type StrategyCardProps = {
  label: string;
  description: string;
  benefits: string[];
  color: string;
};

const StrategyCard = ({ label, description, benefits, color }: StrategyCardProps) => {
  const theme = useTheme();
  return (
    <Stack direction="column" flex={1}>
      <Box
        border={`1px solid ${color}`}
        borderRadius={1}
        marginBottom={-0.5}
        zIndex={1}
        py={1}
        px={2}
        sx={{
          backgroundColor: color,
        }}
      >
        <Typography fontSize={14} fontWeight={600} color="white !important">
          {label}
        </Typography>
      </Box>
      <Box px={1.5} py={2} border={`1px solid ${theme.palette.section.border}`} flex={1}>
        <Typography variant="body2" mb={2}>
          {description}
        </Typography>
        <Typography fontSize={14} fontWeight={600} mb={1}>
          What this means for you
        </Typography>
        <BenefitsList>
          <ul data-testid="investment-changes-list">
            {benefits.map((benefit, index) => (
              <li key={index} data-testid="benefit-item">
                {benefit}
              </li>
            ))}
          </ul>
        </BenefitsList>
      </Box>
    </Stack>
  );
};

type AboutRecommendationsModalProps = {
  open: boolean;
  onClose: () => void;
};

export const AboutRecommendationsModal = ({ open, onClose }: AboutRecommendationsModalProps) => {
  const conservativeStrategy = getStrategyProfile(StrategyProfile.Conservative);
  const moderateStrategy = getStrategyProfile(StrategyProfile.Moderate);
  const aggressiveStrategy = getStrategyProfile(StrategyProfile.Aggressive);

  if (!conservativeStrategy || !moderateStrategy || !aggressiveStrategy) {
    return null;
  }

  const strategies: StrategyCardProps[] = [
    {
      label: `${conservativeStrategy?.label} Strategy`,
      description:
        "Makes small spend adjustments to take advantage of known high-performing channels with strong predicted ROI.",
      benefits: ["Final revenue forecast is predictable", "Low risk of significant under-return"],
      color: conservativeStrategy?.style.backgroundColor,
    },
    {
      label: `${moderateStrategy?.label} Strategy`,
      description:
        "Re-allocates spend to channels with good ROI and scalability, while testing moderate new allocations into higher-potential but less proven channels.",
      benefits: [
        "Final revenue outcome is less predictable",
        "There is a moderate risk of significant under-return.",
        "However, you'll learn more about potential new channels and might overperform expectations.",
      ],
      color: moderateStrategy?.style.backgroundColor,
    },
    {
      label: `${aggressiveStrategy?.label} Strategy`,
      description:
        "Aggressively reallocates spend toward channels with highest revenue potential, regardless of historic spend levels.",
      benefits: [
        "Final revenue outcome is more volatile",
        "Higher risk of significant under-return",
        "Higher potential reward of significant over-performance",
      ],
      color: aggressiveStrategy?.style.backgroundColor,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "40px",
          maxWidth: "900px",
        },
      }}
    >
      <DialogContent sx={{ padding: "40px", position: "relative" }}>
        <Stack spacing={3}>
          <Box>
            <Typography fontSize={20} fontWeight={600} mb={1.5}>
              About Recommendations
            </Typography>
            <Typography fontSize={14} sx={{ maxWidth: "100%" }}>
              Recommendations are automatically generated every time you refresh your data, using
              the same overall spend, end date, scope and geographic range of the initial Action
              Plan.
            </Typography>
            <Typography fontWeight={600} fontSize={14} mt={1.5}>
              Recommendations are generated using the following criteria:
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5}>
            {strategies.map((strategy, index) => (
              <StrategyCard key={index} {...strategy} />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AboutRecommendationsModal;
